import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { useAccountStore } from '@components/Stores/accountStore';
import { getFormulaToAccountById } from '../Dashboard/dashboardUtils';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';
import { getStartDate, sliceFromDate } from '../Policy/MaterialDetails/components/MaterialDetailUtils';
import { HistoricalChart } from './components/HistoricalChart';
import {
  IndexRoutes,
  KeyPoints,
  HistoricalViewOptions,
  PricePoint,
  priceObjectToFutures,
  getCurveForIndex,
} from './components/HistoricalUtils';
//import { default as rebar } from './data/rebar-opportunity.json';
import { useExposureStore, GENERAL_ID } from '../../components/Stores/exposureDataStore';
import { useNavigate, useParams } from 'react-router-dom';
import { getFormulaHistory } from '../../services/materialHistoryService';
import { LoadingSpinnerOverlay } from '../../components/UI-Elements/LoadingSpinnerOverlay';
import { getCurrencySymbol } from '../../hooks/useCurrencyRates';
import { TodayPricePopup } from './components/TodayPricePopup';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';

export const HistoricalPrices: React.FC = () => {
  const navigate = useNavigate();
  const { formulaId } = useParams<{ formulaId: string }>();
  const { account, formulaColorDictionary: colorsMap } = useAccountStore();
  const { setCurrentSelectedFormulaId } = useExposureStore();

  // Configure shared formula card bar — no General, navigate on select
  useCardBar({ includeGeneral: false, onSelectPath: '/historical-prices' });
  
  // Use local state for material selection instead of global exposure store
  // This prevents Historical Prices from overwriting Dashboard's selected material
  const [selFormulaId, setSelFormulaId] = React.useState<string>('');

  React.useEffect(() => {
    const fetchHistoricalPrices = async (matId: string) => {
      setIsLoading(true);
      setGridX(-100);

      console.log('[HistoricalPrices] Fetching history for formulaId:', matId);
      // matId is the base material code (e.g. BUS, HRC) coming from Postgres
      const response = await getFormulaHistory(matId);
      console.log('[HistoricalPrices] Received prices data:', response?.length, 'records');
      console.log('[HistoricalPrices] First price record:', response?.[0]);
      
      // Extract quoteUnit from response if available (API returns it at root level)
      if ((response as any).quoteUnit) {
        console.log('[HistoricalPrices] Setting quoteUnit from API:', (response as any).quoteUnit);
        setQuoteUnit((response as any).quoteUnit);
      }
      
      const prices = Array.isArray(response) ? response : (response as any).history || [];
      setPricesData(prices);

      if (prices && prices.length > 0) {
        const lastPrices = prices[prices.length - 1];
        console.log('[HistoricalPrices] Last price record:', lastPrices);

        const currFutures = priceObjectToFutures(lastPrices) || [];
        console.log('[HistoricalPrices] Extracted futures:', currFutures?.length, 'months');
        setFutures(currFutures);
        setCurrentPrice(lastPrices.price);
      }
      const monthsCount = (prices && prices.length > 0 && (priceObjectToFutures(prices[prices.length - 1]) || []).length) || 0;
      setMaxFutures(monthsCount);

      if (monthsCount === 0) {
        setSelectedFuture(0);
      } else if (selectedFuture >= monthsCount) {
        setSelectedFuture(Math.min(2, monthsCount - 1));
      }

      setIsLoading(false);
    };

    const hasFormulas = account.formulasToAccount?.length > 0;
    const routeFormulaInAccount = formulaId
      ? account.formulasToAccount?.some((m) => m.formulaId === formulaId)
      : false;
    console.log('[HistoricalPrices] Effect triggered - formulaId:', formulaId, 'hasFormulas:', hasFormulas, 'routeFormulaInAccount:', routeFormulaInAccount);

    if ((!formulaId || formulaId === '') && hasFormulas) {
      const firstFormula = account.formulasToAccount.filter((a) => a.formulaId !== GENERAL_ID)?.[0]?.formulaId;
      console.log('[HistoricalPrices] No formulaId in route, redirecting to first formula:', firstFormula);
      if (firstFormula) {
        setFormula(firstFormula);
      }

      return;
    }
    if (!hasFormulas || (formulaId && !routeFormulaInAccount)) {
      // Account has no formulas or the routed formulaId is not valid for this account
      // Clear local chart state so the empty state is shown instead of stale data
      setSelFormulaId(GENERAL_ID);
      setPricesData(undefined);
      setFutures([]);
      setPricePoints([]);
      setMaxFutures(0);
      setCurrentPrice(0);
      setGridX(0);
      setSelectedFuture(0);
      setIsLoading(false);
      return;
    }

    setSelFormulaId(formulaId || GENERAL_ID);
    // Sync global store so card bar highlights correctly
    if (formulaId) setCurrentSelectedFormulaId(formulaId);
    if (formulaId) {
      console.log('[HistoricalPrices] Calling fetchHistoricalPrices for formulaId:', formulaId);
      fetchHistoricalPrices(formulaId);
    } else {
      console.log('[HistoricalPrices] No formulaId to fetch, skipping API call');
    }
  }, [formulaId, account.formulasToAccount]);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [monthsToShow, setMonthsToShow] = React.useState<number>(12);
  const [selectedFuture, setSelectedFuture] = React.useState<number>(2);
  const [maxFutures, setMaxFutures] = React.useState<number>(5);

  const [pricesData, setPricesData] = React.useState<any[]>();
  const [futures, setFutures] = React.useState<any[]>([]);
  const [currentPrice, setCurrentPrice] = React.useState<number>(0);
  const [pricePoints, setPricePoints] = React.useState<PricePoint[]>([]);
  const [gridX, setGridX] = React.useState<number>(0);
  const [showTodayPopup, setShowTodayPopup] = React.useState(false);
  const [quoteUnit, setQuoteUnit] = React.useState<string | null>(null);

  React.useEffect(() => {
    if ((!selFormulaId || selFormulaId === GENERAL_ID) && account?.formulasToAccount?.length > 1) {
      setSelFormulaId(account.formulasToAccount[1].formulaId);
    }
  }, [account.formulasToAccount, selFormulaId]);

  const getFuture = (pData: any, futureCompare: number): number => {
    const months = Array.from({ length: 12 }, (_, i) => pData['month' + String(i + 1).padStart(2, '0')]).filter(
      Boolean,
    );

    return months[futureCompare];
  };

  React.useEffect(() => {
    if (pricesData && pricesData.length > 0) {
      const lastPrices = pricesData[pricesData.length - 1];

      onDataClick(
        { date: '', future: getFuture(lastPrices, selectedFuture), price: lastPrices.price?.toString() || '' },
        -100,
      );
    }
  }, [selectedFuture, monthsToShow, pricesData]);

  const getVisibleData = (
    pricesDat: any[],
    showCount: number,
    futureCompare: number,
    futuresPr: any[],
  ): IndexRoutes[] => [
    ...sliceFromDate(pricesDat, getStartDate(showCount)).map((s, i) => ({
      price: s.price,
      future: getFuture(s, futureCompare),
      date: s.date,
      index: i,
    })),
    ...futuresPr,
  ];
  const onDataClick = ({ price, future, isFuture, ...data }: IndexRoutes, xCoord: number) => {
    const currPrice = pricesData?.[pricesData.length - 1];

    const nPrice = +(price || 0);
    const date = isFuture ? new Date(currPrice.date) : data.date ? new Date(data.date) : new Date();

    const indexDateString = isFuture ? currPrice?.date : data.date;
    const formattedIndexDate = indexDateString ? format(new Date(indexDateString), 'd MMM yyyy') : '';
    const updatedPoints = [
      {
        name: 'Index price',
        price: +(price || currentPrice) || undefined,
        color: colorsMap[selFormulaId],
        id: 'index',
        visible: true,
        asOfDate: formattedIndexDate || undefined,
      },
      {
        name: `Future (${selectedFuture + 1}) price`,
        price: future,
        color: '#19ceb8',
        id: 'future1',
        visible: true,
      },
      ...(future
        ? [
            {
              name: `${future - nPrice < 0 ? 'Backwardation' : 'Contago'} (${selectedFuture + 1})`,
              price: future - nPrice,
              percent: ((future - nPrice) / nPrice || 1) * 100,
              color: '#19ceb8',
              id: 'compare',
              visible: true,
            },
          ]
        : []),
      ...getCurveForIndex(
        data.index || (pricesData?.length || 1) - 1,
        date,
        sliceFromDate(pricesData || [], getStartDate(monthsToShow)),
      ),
    ];
    setPricePoints(updatedPoints);
    setGridX(xCoord);
  };

  const setFormula = (formulaId: string) => {
    setSelFormulaId(formulaId);

    navigate('/historical-prices/' + (formulaId && formulaId !== GENERAL_ID ? formulaId : ''));
  };

  const { materialsMap } = useMaterialsStore();
  const selectedFormula = getFormulaToAccountById(account, selFormulaId);
  const hasSelectedFormula = account.formulasToAccount.some((m) => m.formulaId === selFormulaId);
  const isRatio = materialsMap[selFormulaId]?.contractType === 'ratio';

  const formulaTitleName =
    selectedFormula?.displayName || selectedFormula?.formulaId || formulaId || 'selected formula';
  const formulaTitleSymbol =
    selectedFormula?.quoteSourceSymbol || selectedFormula?.formulaId || formulaId || '';

  return (
    <div className="flex flex-col flex-1 h-full gap-2">
      {selFormulaId ? (
        <div className="flex flex-col bg-white shadow-custom py-[0.75rem] gap-[1rem] px-[1.5rem] mt-1">
          <div className="flex flex-1 flex-col gap-2">
            <h4 className="text-base font-semibold text-gray-800">
              Historical prices for
              {' '}
              {formulaTitleName}
              {formulaTitleSymbol && (
                <span className="text-xs text-gray-500 ml-1">({formulaTitleSymbol})</span>
              )}
              {pricesData && pricesData.length > 0 && pricesData[pricesData.length - 1]?.date && (
                <>
                  <span className="text-gray-300 mx-2">·</span>
                  <span className="text-xs font-normal text-gray-400">
                    last updated {format(new Date(pricesData[pricesData.length - 1].date), 'MMM d, yyyy')}
                  </span>
                  <span className="text-[11px] font-normal text-gray-300 ml-1">
                    ({formatDistanceToNow(new Date(pricesData[pricesData.length - 1].date), { addSuffix: true })})
                  </span>
                </>
              )}
            </h4>
            {pricesData ? (
              <div className="flex flex-row flex-1 gap-10 overflow-hidden items-start">
                <div className="flex flex-col flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">Futures</span>
                      {maxFutures > 0 && (
                        <SegmentedControl
                          options={Array.from({ length: maxFutures }).map((_, i) => ({
                            label: `${i + 1}`,
                            value: i,
                          }))}
                          selected={selectedFuture}
                          onChange={setSelectedFuture}
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => hasSelectedFormula && setShowTodayPopup(true)}
                        disabled={!hasSelectedFormula}
                        className={`rounded-full px-5 py-2 text-sm font-semibold shadow-sm transition-colors ${
                          hasSelectedFormula
                            ? 'bg-blue-600 text-white hover:bg-blue-600/90'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        ⚡ Latest Prices
                      </button>
                      {showTodayPopup &&
                        account.formulasToAccount.some((m) => m.formulaId === selFormulaId) && (
                          <TodayPricePopup
                            onClose={() => setShowTodayPopup(false)}
                            material={
                              account.formulasToAccount.find((m) => m.formulaId === selFormulaId)!
                            }
                            historyPrices={pricesData || []}
                            quoteUnit={quoteUnit}
                            isRatio={isRatio}
                          />
                        )}
                      <span className="text-xs text-gray-500 ml-4">Range</span>
                      <SegmentedControl
                        options={HistoricalViewOptions.map(({ value }) => ({
                          value,
                          label:
                            value % 12 === 0
                              ? `${value / 12}Y`
                              : `${value}m`,
                        }))}
                        selected={monthsToShow}
                        onChange={setMonthsToShow}
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <HistoricalChart
                      indexRoutesData={getVisibleData(pricesData || [], monthsToShow, selectedFuture, futures)}
                      materialColor={colorsMap[selFormulaId]}
                      onDataClick={onDataClick}
                      currency={isRatio ? '' : (selectedFormula.currency || 'USD')}
                      lineXcoord={gridX}
                      isDrawPrice
                    />
                  </div>
                  <div className="pt-4 h-[240px] mb-2">
                    <HistoricalChart
                      indexRoutesData={getVisibleData(pricesData || [], monthsToShow, selectedFuture, futures)}
                      materialColor={colorsMap[selFormulaId]}
                      onDataClick={onDataClick}
                      currency={isRatio ? '' : (selectedFormula.currency || 'USD')}
                      lineXcoord={gridX}
                      compact
                    />
                  </div>
                </div>
                <div className="w-[16rem] flex-shrink-0 pl-4 border-l border-gray-200">
                  <KeyPoints
                    routePrices={pricePoints}
                    selectedRoute={`Future ${selectedFuture}`}
                    currency={isRatio ? '' : getCurrencySymbol(selectedFormula.currency || 'USD')}
                    currencyCode={isRatio ? undefined : (selectedFormula.currency || undefined)}
                    materialColor={colorsMap[selFormulaId]}
                  />
                </div>
              </div>
            ) : (
              <div className="flex h-[30rem] justify-center items-center">
                {!isLoading && (
                  <div className="text-center">
                    No futures curve available yet for
                    {' '}
                    {selectedFormula.displayName || selectedFormula.formulaId || formulaId}
                    {selectedFormula.formulaId && ` (${selectedFormula.formulaId})`}
                  </div>
                )}
              </div>
            )}
            {isLoading && <LoadingSpinnerOverlay size={64} />}
          </div>
        </div>
      ) : (
        <HistPricesSkeleton />
      )}
    </div>
  );
};

const HistPricesSkeleton: React.FC = () => <div>loading...</div>;

interface SegmentedOption {
  label: string;
  value: number;
}

interface SegmentedControlProps {
  options: SegmentedOption[];
  selected: number;
  onChange: (value: number) => void;
}

const SegmentedControl: React.FC<SegmentedControlProps> = ({ options, selected, onChange }) => {
  return (
    <div className="inline-flex items-center gap-1 bg-gray-100 rounded-full p-1">
      {options.map((opt) => {
        const isActive = opt.value === selected;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              isActive
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

export default HistoricalPrices;
